import {Col, Form, Input, InputNumber, Radio, RadioChangeEvent, Row, Select} from 'antd';
import {FormInstance} from 'antd/lib/form';
import React, {useContext, useEffect, useState} from 'react';
import {SelectProps} from "antd/es/select";
import {FilterDialogContext, FilterDialogContextType} from "./FilterDialogContext";
import './CohortsFilter.scss'
import {apiFetchOptions} from "../../../create/selectorApi";
import {LabelAndKey} from "../../../create/types/LabelAndKey";
import {cohortMonthsField, cohortPeriodField} from "../../QueryBuilder";
import _ from "lodash";
import {LabelAndValue} from "../../../create/types/LabelAndValue";
import ToolTip, {MONTH_DESCRIPTION, PERIOD_DESCRIPTION} from "../../../common/ToolTip";

type PERIOD_VALUE = "before" | "beforeinc" | "on" | "afterinc" | "after"

const PERIOD_LIST = [
    {label: 'Before Index', value: 'before'},
    {label: 'Before Index + On Index', value: 'beforeinc'},
    {label: 'On Index', value: 'on'},
    {label: 'On Index + After Index', value: 'afterinc'},
    {label: 'After Index', value: 'after'},
]

const CUSTOM_MONTH = {label: 'Custom months', value: 'custom'}
const MONTHS_LIST = [
    {label: 'All months', value: '120'},
    {label: '30 months', value: '30'},
    {label: '24 months', value: '24'},
    {label: '18 months', value: '18'},
    {label: '12 months', value: '12'},
    {label: '6 months', value: '6'}
]

export const cohortDescription = (
    code: string,
    period: string,
    month: string,
    cohorts: LabelAndValue[],
    choices: LabelAndKey[]
) => {
    const matchingChoices = choices
        .filter(i => cohorts.map(i => i.value).includes(i.key))

    const periodLabel = _.find(PERIOD_LIST, {value: period})!.label
    const monthLabel = month === '120' ? 'All months' : `${month} months`

    const descriptions = matchingChoices
        .map(i => `Cohort - ${i.label}: ${i.opt.description}`)
        .join(' & ')

    return {
        field: code,
        title: 'Cohort Activities',
        values: [`${monthLabel} ${periodLabel}, ${descriptions}`]
    }
}

interface CohortsFilterProps<ValueType = any> extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
    form: FormInstance
    fieldId: string
    cohortId: number | null
}

const CohortsFilter = ({form, fieldId, cohortId, ...props}: CohortsFilterProps) => {
    const PERIOD_FIELD = cohortPeriodField(fieldId)
    const MONTHS_FIELD = cohortMonthsField(fieldId)

    const [cohortChoices, setCohortChoices] = useState<LabelAndKey[] | undefined>(undefined)
    const [uiMonths, setUIMonths] = useState<string | null>(null)
    const [customMonthEnabled, setCustomMonthEnabled] = useState<boolean>(false)
    const [customMonthValue, setCustomMonthValue] = useState<number | null>(null)
    const [periodValue, setPeriodValue] = useState<PERIOD_VALUE>("before")

    const {
        setAllButtonStatus,
    } = useContext(FilterDialogContext) as FilterDialogContextType

    useEffect(() => {
        setAllButtonStatus('hide')
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        apiFetchOptions('/cohorts?cohortId=' + cohortId, setCohortChoices)
    }, [cohortId])

    const [init, setInit] = useState<boolean>(false)
    const formMonth = form.getFieldValue(MONTHS_FIELD)
    useEffect(() => {
        if (!init && formMonth) {
            const presetMonth = MONTHS_LIST.map(i => i.value).includes(formMonth)
            setCustomMonthEnabled(!presetMonth)
            if (!presetMonth) {
                setUIMonths(CUSTOM_MONTH.value)
            } else {
                setUIMonths(formMonth)
            }
            setCustomMonthValue(formMonth)
            setInit(true)
        }
    }, [formMonth]) // eslint-disable-line react-hooks/exhaustive-deps

    const onPeriodChange = (e: RadioChangeEvent) => {
        const period = e.target.value
        form.setFieldValue(PERIOD_FIELD, period)
        updateLabels()
        setPeriodValue(period)
    }

    const onMonthsChange = (e: RadioChangeEvent) => {
        const months = e.target.value
        setUIMonths(months)
        if (months !== CUSTOM_MONTH.value) {
            setCustomMonthValue(months)
            form.setFieldValue(MONTHS_FIELD, months)
        }
        updateLabels()
        setCustomMonthEnabled(months === CUSTOM_MONTH.value)
    }

    const onCustomMonthsChange = (months: number | null) => {
        if (months) {
            setCustomMonthValue(months)
            form.setFieldValue(MONTHS_FIELD, months)
        }
        updateLabels()
    }

    const onSelectChange = (values: string[]) => {
        updateLabels()
    }

    const updateLabels = () => {
        const description = cohortDescription(
            fieldId,
            form.getFieldValue(PERIOD_FIELD),
            form.getFieldValue(MONTHS_FIELD),
            form.getFieldValue(fieldId),
            cohortChoices!
        )
        form.setFieldValue(`${fieldId}_labels`, description.values)
    }

    const isMonthsDisabled = periodValue === "on"

    return <Row>
        <Col span={20} style={{padding: "5px"}}>
            <Form.Item name={`${fieldId}_labels`} hidden={true}>
                <Input/>
            </Form.Item>
            <Form.Item data-testid={'cohort-choice'}>
                <Form.Item name={fieldId} noStyle={true} >
                    <Select
                        mode="multiple"
                        onChange={onSelectChange}
                        options={cohortChoices ? cohortChoices.map(i => {
                            return {value: i.key, label: i.label}
                        }) : undefined}
                        labelInValue={true}
                    />
                </Form.Item>
            </Form.Item>
        </Col>
        <Col span={24}>
            <Form.Item name={PERIOD_FIELD} className={'radio-group'} data-testid={'cohort-period'}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Radio.Group
                        value={form.getFieldValue(PERIOD_FIELD)}
                        onChange={onPeriodChange}
                    >
                        {PERIOD_LIST.map(i => (
                            <Radio key={i.value} value={i.value}>
                                {i.label}
                            </Radio>
                        ))}
                    </Radio.Group>


                    <div style={{ marginLeft: 'auto' }}>
                        <ToolTip content={PERIOD_DESCRIPTION} />
                    </div>
                </div>
            </Form.Item>
        </Col>
        <Col span={24}>
            <Form.Item name={MONTHS_FIELD} hidden={true}>
                <Input/>
            </Form.Item>
            <Form.Item className={'radio-group'} data-testid={'cohort-months'}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Radio.Group
                            value={uiMonths}
                            onChange={onMonthsChange}
                            size="middle"
                            buttonStyle="solid"
                            disabled={isMonthsDisabled}
                        >
                            <div>
                                {[...MONTHS_LIST].map(i => (
                                    <Radio.Button key={i.value} value={i.value}>
                                        {i.label}
                                    </Radio.Button>
                                ))}
                            </div>
                            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Radio.Button value={CUSTOM_MONTH.value} disabled={isMonthsDisabled}>
                                    {CUSTOM_MONTH.label}
                                </Radio.Button>
                                <InputNumber
                                    value={customMonthValue}
                                    min={0}
                                    max={120}
                                    onChange={onCustomMonthsChange}
                                    size="middle"
                                    style={{ width: '60px' }}
                                    disabled={!customMonthEnabled || isMonthsDisabled}
                                />
                            </div>
                        </Radio.Group>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                        <ToolTip content={MONTH_DESCRIPTION} />
                    </div>
                </div>
            </Form.Item>

        </Col>
    </Row>
}

export default CohortsFilter
